import { z } from 'zod';

import type { VehicleLastPositionResponseType } from '../../Vehicles/types';
import { VehiclesApi } from '../../Vehicles/VehiclesApi';
import { Api } from '@automapa/svelte-automapa-template';
import { Vehicle } from '../../Vehicles/Vehicle';
import type { Assignment } from '../Assignment';
import { PointOnMap } from './PointOnMap';

export interface IVehicleOnMap {
    assignment: Assignment;
    vehicle: Vehicle;
    pointOnMap: PointOnMap;
    nextJobPoint: PointOnMap;
}

export const VehicleOnMapSettings = {
    update_interval_seconds: 10,
    idle_indicator_seconds: 5 * 60,
    inactive_indicator_seconds: 60 * 60,
    visible: true,
    random_mode: false,
};

export const VehicleOnMapSettingsSchema = z
    .object({
        update_interval_seconds: z.number().default(VehicleOnMapSettings.update_interval_seconds),
        idle_indicator_seconds: z.number().default(VehicleOnMapSettings.idle_indicator_seconds),
        inactive_indicator_seconds: z
            .number()
            .default(VehicleOnMapSettings.inactive_indicator_seconds),
        visible: z.boolean().default(VehicleOnMapSettings.visible),
        random_mode: z.boolean().default(VehicleOnMapSettings.random_mode),
    })
    .catch(VehicleOnMapSettings);
export type VehicleOnMapSettingsType = z.infer<typeof VehicleOnMapSettingsSchema>;

export class VehiclesOnMap {
    private readonly api: Api;
    private lastPositions: VehicleLastPositionResponseType | null = null;
    private pointsToTrack: IVehicleOnMap[] = [];
    private settings: VehicleOnMapSettingsType;
    private updateIntervalHandler: any;
    private destroyed: boolean = false;
    private debug: boolean = false;

    constructor(api: Api) {
        this.settings = { ...VehicleOnMapSettings };
        this.api = api;
    }

    public setPointsToTrack(vehicles: Vehicle[], assignments: Assignment[]): this {
        this.debug &&
            console.debug('vehiclesOnMap, setPointsToTrack', vehicles.length, assignments.length);
        this.removePoints();
        if (this.destroyed) {
            console.debug('vehiclesOnMap, setPointsToTrack, destroyed');
            return this;
        }
        vehicles.forEach(vehicle => {
            const pointOnMap = new PointOnMap();
            const nextJobPoint = new PointOnMap();
            const assignment = assignments.find(
                assignment => assignment.getVehicleId() === vehicle.getId()
            );
            if (assignment === undefined) {
                console.warn('Assignment not found for vehicle ' + vehicle.getName());
            } else {
                this.pointsToTrack.push({ vehicle, pointOnMap, assignment, nextJobPoint });
            }
        });
        this.update();
        return this;
    }

    public setVisibility(visible: boolean): this {
        this.settings.visible = visible;
        this.drawOrClearAll();
        return this;
    }

    public setRandomMode(randomMode: boolean): this {
        this.settings.random_mode = randomMode;
        return this;
    }

    public setUpdateIntervalSeconds(interval_seconds: number): this {
        this.clearIntervals();
        this.settings.update_interval_seconds = interval_seconds;
        this.update();
        return this;
    }

    public destroy() {
        this.destroyed = true;
        this.clearIntervals();
        this.removePoints();
    }

    public removePoints() {
        this.removeAllFromMap();
        this.pointsToTrack = [];
    }

    private setTimeout(interval: number): this {
        this.clearIntervals();
        this.updateIntervalHandler = setTimeout(async () => {
            await this.update();
        }, interval);
        return this;
    }

    private clearIntervals() {
        if (this.updateIntervalHandler) {
            clearTimeout(this.updateIntervalHandler);
        }
    }

    public getIdsToTrack(): number[] {
        return this.pointsToTrack
            .filter(point => point.vehicle.getId() !== null)
            .map(point => point.assignment.getId() as number);
    }

    public drawOrClearAll() {
        if (this.settings.visible && this.lastPositions !== null && !this.destroyed) {
            this.pointsToTrack.forEach(position => {
                let vid = position.vehicle.getId();
                if (vid !== null) {
                    const lastPosition = this.lastPositions ? this.lastPositions[vid] : null;
                    if (lastPosition) {
                        position.pointOnMap.drawPoint(
                            lastPosition.x,
                            lastPosition.y,
                            lastPosition.ts,
                            position.vehicle.getColor(),
                            this.getOpacityByActivity(lastPosition.ts, position),
                            position,
                            lastPosition.parking
                        );
                    } else {
                        console.warn('vehiclesOnMap, drawOrClearAll, lastPosition is null');
                    }
                } else {
                    console.warn('vehiclesOnMap, drawOrClearAll, vehicle id is null');
                }
            });
        } else {
            this.removeAllFromMap();
        }
    }

    public removeAllFromMap() {
        this.pointsToTrack.forEach(point => {
            point.pointOnMap.removePoint();
            point.nextJobPoint.removePoint(); // DEPRECATED
        });
    }

    public getOpacityByActivity(timestamp: string, position: IVehicleOnMap) {
        let ts = new Date(timestamp).getTime();
        let now = new Date().getTime();
        let diff = now - ts;
        let opacity = 1;
        if (diff > this.settings.idle_indicator_seconds * 1000) {
            opacity = 0.5;
        } else if (diff > this.settings.inactive_indicator_seconds * 1000) {
            opacity = 0.2;
        }
        return opacity;
    }

    public async update() {
        this.debug && console.debug('vehiclesOnMap, update');
        this.clearIntervals();
        if (this.getIdsToTrack().length > 0) {
            let res = await VehiclesApi.getLastPositions(
                this.api,
                this.getIdsToTrack(),
                this.settings.random_mode
            ).catch(err => {
                console.error(err);
            });
            if (res) {
                this.debug && console.debug('vehiclesOnMap, update, lastPositions', res);
                this.lastPositions = res;
            } else {
                this.debug && console.warn('vehiclesOnMap, update, lastPositions is null');
            }
        } else {
            this.debug && console.warn('vehiclesOnMap, update, getIdsToTrack is empty');
        }
        this.drawOrClearAll();
        this.setTimeout(this.settings.update_interval_seconds * 1000);
    }
}
